import {
  getSentilinkReasonDescription,
  isSentilinkRiskRaisingDirection,
} from "shared/config/sentilinkReasonCodes";

import type {
  SentilinkData,
  SentilinkManualVerification,
  SentilinkNameMismatchDetails,
  SentilinkReasonCode,
} from "../interfaces";

const OUTCOMES = new Set(["PASS", "REVIEW", "BLOCK", "ERROR"]);
const RISK_TIERS = new Set(["LOW_RISK", "NEEDS_REVIEW", "HIGH_RISK"]);

function resolveNoUsSsnReason(app: Record<string, unknown>): string | null {
  const summary = app.applicationSummary as Record<string, unknown> | undefined;
  const raw = app.sentilinkNoUsSsnReason ?? summary?.sentilinkNoUsSsnReason;
  if (typeof raw !== "string") {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function parseCount(value: unknown): number | undefined {
  if (value == null || value === "") {
    return undefined;
  }
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function resolveRecordId(
  app: Record<string, unknown>
): Pick<SentilinkData, "recordId"> {
  const result =
    app.sentilinkResult && typeof app.sentilinkResult === "object"
      ? (app.sentilinkResult as Record<string, unknown>)
      : undefined;
  const recordId = parseCount(app.sentilinkRecordId ?? result?.id);
  return recordId == null ? {} : { recordId };
}

/** Manager reset counters for the Identity Risk Check (PRODUCT-1346). */
function resolveResetCounts(
  app: Record<string, unknown>
): Pick<SentilinkData, "resetCount" | "resetLimit"> {
  const result = app.sentilinkResult;
  const nested =
    result && typeof result === "object"
      ? (result as Record<string, unknown>)
      : undefined;
  return {
    resetCount: parseCount(app.sentilinkResetCount ?? nested?.resetCount),
    resetLimit: parseCount(app.sentilinkResetLimit ?? nested?.resetLimit),
  };
}

function parseBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function normalizeManualVerification(
  value: unknown
): SentilinkManualVerification | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const row = value as Record<string, unknown>;
  return {
    reviewerName:
      row.reviewerName == null ? null : String(row.reviewerName),
    reviewerEmail:
      row.reviewerEmail == null ? null : String(row.reviewerEmail),
    reviewedItem:
      row.reviewedItem == null ? null : String(row.reviewedItem),
    reviewedItemLabel:
      row.reviewedItemLabel == null ? null : String(row.reviewedItemLabel),
    details: row.details == null ? null : String(row.details),
    verifiedAt: row.verifiedAt == null ? null : String(row.verifiedAt),
  };
}

function resolveManualVerification(
  app: Record<string, unknown>
): Pick<
  SentilinkData,
  | "identityManuallyVerified"
  | "identityManualVerification"
  | "sentilinkActiveForIdentityDecisioning"
> {
  const result =
    app.sentilinkResult && typeof app.sentilinkResult === "object"
      ? (app.sentilinkResult as Record<string, unknown>)
      : undefined;
  const identityManuallyVerified = Boolean(
    app.identityManuallyVerified ?? result?.identityManuallyVerified
  );
  return {
    identityManuallyVerified,
    identityManualVerification: normalizeManualVerification(
      app.identityManualVerification ?? result?.identityManualVerification
    ),
    sentilinkActiveForIdentityDecisioning: parseBoolean(
      app.sentilinkActiveForIdentityDecisioning ??
        result?.sentilinkActiveForIdentityDecisioning
    ),
  };
}

function notTriggeredSentilinkData(app: Record<string, unknown>): SentilinkData {
  const noUsSsnReason = resolveNoUsSsnReason(app);
  const notTriggeredStatus = noUsSsnReason ? "SKIPPED_NO_SSN" : "NOT_STARTED";

  return {
    ...resolveRecordId(app),
    triggered: false,
    score: null,
    reasonCodes: [],
    outcome: null,
    riskTier: null,
    transactionId: null,
    calledAt: null,
    notTriggeredStatus,
    ...resolveResetCounts(app),
    ...resolveManualVerification(app),
    ...(notTriggeredStatus === "SKIPPED_NO_SSN" ? { noUsSsnReason } : {}),
  };
}

/** Manager UI flat payload always includes ``sentilinkReasonCodes`` (even when empty). */
function hasSentilinkFlatManagerFields(
  rawApp: Record<string, unknown>
): boolean {
  return Object.prototype.hasOwnProperty.call(rawApp, "sentilinkReasonCodes");
}

function parseCalledAt(value: unknown): string | null {
  if (value == null || value === "") {
    return null;
  }
  return String(value);
}

function resolveCalledAt(app: Record<string, unknown>): string | null {
  const fromFlat = parseCalledAt(app.sentilinkCalledAt);
  if (fromFlat) {
    return fromFlat;
  }
  const result = app.sentilinkResult;
  if (result && typeof result === "object") {
    return parseCalledAt((result as Record<string, unknown>).calledAt);
  }
  return null;
}

function resolveTransactionId(app: Record<string, unknown>): string | null {
  if (app.sentilinkTransactionId != null) {
    return String(app.sentilinkTransactionId);
  }
  const result = app.sentilinkResult;
  if (result && typeof result === "object") {
    const id = (result as Record<string, unknown>).transactionId;
    return id == null ? null : String(id);
  }
  return null;
}

function resolveNameMismatch(
  app: Record<string, unknown>
): Pick<SentilinkData, "nameIdentityMismatch" | "nameMismatchDetails"> {
  const mismatch = app.sentilinkNameIdentityMismatch;
  if (mismatch == null) {
    // Backend omits this field for non-conclusive checks (ERROR/SKIPPED/never ran);
    // do not default to false, which would read as "verified clean".
    return {};
  }
  if (mismatch !== true) {
    return { nameIdentityMismatch: false };
  }
  const raw = app.sentilinkNameMismatchDetails;
  if (!raw || typeof raw !== "object") {
    return { nameIdentityMismatch: true, nameMismatchDetails: null };
  }
  const row = raw as Record<string, unknown>;
  const details: SentilinkNameMismatchDetails = {
    submittedName:
      row.submitted_name == null ? null : String(row.submitted_name),
    idCredentialName:
      row.id_credential_name == null ? null : String(row.id_credential_name),
    personaName: row.persona_name == null ? null : String(row.persona_name),
    manualName: row.manual_name == null ? null : String(row.manual_name),
    anchorUsed: row.anchor_used == null ? null : String(row.anchor_used),
  };
  return { nameIdentityMismatch: true, nameMismatchDetails: details };
}

function resolveSubmittedName(
  app: Record<string, unknown>
): Pick<SentilinkData, "submittedFirstName" | "submittedLastName"> {
  const hasFirst = Object.prototype.hasOwnProperty.call(
    app,
    "sentilinkSubmittedFirstName"
  );
  const hasLast = Object.prototype.hasOwnProperty.call(
    app,
    "sentilinkSubmittedLastName"
  );
  if (!hasFirst && !hasLast) {
    return {};
  }
  const normalize = (value: unknown): string | null => {
    if (value == null) {
      return null;
    }
    const trimmed = String(value).trim();
    return trimmed || null;
  };
  return {
    submittedFirstName: normalize(app.sentilinkSubmittedFirstName),
    submittedLastName: normalize(app.sentilinkSubmittedLastName),
  };
}

function parseScore(value: unknown): number | null {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "string" && value.trim() === "") {
    return null;
  }
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Fallback when ``GET /applications/:id/full/`` returns PDF/report SentiLink fields
 * (``sentilinkTriggered`` + ``sentilinkResult``) but not flat manager fields — e.g.
 * flat merge gated on Lumen sensitive access or backend not yet deployed.
 *
 * When ``sentilinkTriggered`` is false and flat fields are absent, SentiLink is not
 * in scope (``Company.enableIdfraudrisk`` off).
 * Do not map to a not-triggered UI shape — callers should hide SentiLink entirely.
 */
function mapFromSentilinkPdfPayload(
  app: Record<string, unknown>
): SentilinkData | undefined {
  if (!Object.prototype.hasOwnProperty.call(app, "sentilinkTriggered")) {
    return undefined;
  }

  const triggered = Boolean(app.sentilinkTriggered);
  if (!triggered) {
    return undefined;
  }

  const result = app.sentilinkResult;
  if (!result || typeof result !== "object") {
    return {
      ...resolveRecordId(app),
      triggered: true,
      score: null,
      reasonCodes: [],
      outcome: "ERROR",
      riskTier: null,
      transactionId: resolveTransactionId(app),
      calledAt: resolveCalledAt(app),
      ...resolveResetCounts(app),
      ...resolveManualVerification(app),
    };
  }

  const row = result as Record<string, unknown>;
  const gateOutcome = String(row.gateOutcome ?? "");

  if (gateOutcome === "Error") {
    return {
      ...resolveRecordId(app),
      triggered: true,
      score: null,
      reasonCodes: [],
      outcome: "ERROR",
      riskTier: null,
      transactionId: resolveTransactionId(app),
      calledAt: resolveCalledAt(app),
      ...resolveResetCounts(app),
      ...resolveManualVerification(app),
    };
  }

  const score = parseScore(row.score);
  const outcome =
    gateOutcome === "Pass"
      ? "PASS"
      : gateOutcome === "Fail"
        ? "BLOCK"
        : null;

  return {
    ...resolveRecordId(app),
    triggered: true,
    score,
    reasonCodes: mapReasonCodes(row.reasonCodes),
    outcome,
    riskTier: normalizeRiskTier(row.riskTier ?? app.sentilinkRiskTier),
    transactionId: resolveTransactionId(app),
    calledAt: resolveCalledAt(app),
    ...resolveSubmittedName(app),
    ...resolveResetCounts(app),
    ...resolveManualVerification(app),
  };
}

function normalizeOutcome(
  value: unknown
): SentilinkData["outcome"] {
  if (value == null || value === "") {
    return null;
  }
  const s = String(value).toUpperCase();
  return OUTCOMES.has(s) ? (s as SentilinkData["outcome"]) : null;
}

function normalizeRiskTier(
  value: unknown
): SentilinkData["riskTier"] {
  if (value == null || value === "") {
    return null;
  }
  const s = String(value).toUpperCase();
  return RISK_TIERS.has(s) ? (s as SentilinkData["riskTier"]) : null;
}

function parseReasonRank(row: Record<string, unknown>): number {
  const raw = row.rank ?? row.Rank;
  if (raw == null || typeof raw === "boolean") {
    return Number.POSITIVE_INFINITY;
  }
  if (typeof raw === "string" && raw.trim() === "") {
    return Number.POSITIVE_INFINITY;
  }
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

function mapReasonCode(
  item: unknown
): (SentilinkReasonCode & { rank: number }) | null {
  if (!item || typeof item !== "object") {
    return null;
  }
  const row = item as Record<string, unknown>;
  const direction = String(row.direction ?? row.Direction ?? "");
  if (!isSentilinkRiskRaisingDirection(direction)) {
    return null;
  }
  const code = String(row.code ?? row.Code ?? "");
  const fromApi = row.description ?? row.Description;
  const fromExplanation = row.explanation ?? row.Explanation;
  const fallback =
    typeof fromApi === "string"
      ? fromApi
      : typeof fromExplanation === "string"
      ? fromExplanation
      : undefined;
  const description = getSentilinkReasonDescription(code, fallback);
  if (!description) {
    return null;
  }
  return { code, description, rank: parseReasonRank(row) };
}

function mapReasonCodes(raw: unknown): SentilinkReasonCode[] {
  if (raw == null || !Array.isArray(raw)) {
    return [];
  }
  const mapped = raw
    .map(mapReasonCode)
    .filter(
      (row): row is SentilinkReasonCode & { rank: number } => row != null
    );
  mapped.sort((a, b) => a.rank - b.rank);
  return mapped.map(({ code, description }) => ({ code, description }));
}

/**
 * Maps deal-summary / full?mode=deal application payload (camelCase keys) to SentilinkData.
 * Returns undefined when the API omits SentiLink (company not entitled / pre–SentiLink).
 *
 * Flat manager fields (``sentilinkReasonCodes``) are only merged when
 * ``Company.enableIdfraudrisk`` is on; not-triggered UI
 * (NOT STARTED / SKIPPED) requires those fields. PDF-only ``sentilinkTriggered: false``
 * is treated as out-of-scope so the verifications page stays unchanged.
 */
export function mapSentilink(rawApp: unknown): SentilinkData | undefined {
  if (!rawApp || typeof rawApp !== "object") {
    return undefined;
  }
  const app = rawApp as Record<string, unknown>;

  if (!hasSentilinkFlatManagerFields(app)) {
    return mapFromSentilinkPdfPayload(app);
  }

  const triggered = Boolean(app.sentilinkTriggered);

  if (!triggered) {
    return notTriggeredSentilinkData(app);
  }

  const score = parseScore(app.sentilinkScore);

  return {
    ...resolveRecordId(app),
    triggered: true,
    score,
    reasonCodes: mapReasonCodes(app.sentilinkReasonCodes),
    outcome: normalizeOutcome(app.sentilinkOutcome),
    ...(app.sentilinkResponseStatus != null
      ? { responseStatus: String(app.sentilinkResponseStatus) }
      : {}),
    riskTier: normalizeRiskTier(app.sentilinkRiskTier),
    syntheticScore: parseScore(app.sentilinkSyntheticScore),
    syntheticScoreVersion:
      app.sentilinkSyntheticScoreVersion == null
        ? null
        : String(app.sentilinkSyntheticScoreVersion),
    syntheticReasonCodes: mapReasonCodes(app.sentilinkSyntheticReasonCodes),
    syntheticOutcome: normalizeOutcome(app.sentilinkSyntheticOutcome),
    syntheticRiskTier: normalizeRiskTier(app.sentilinkSyntheticRiskTier),
    transactionId: resolveTransactionId(app),
    calledAt: resolveCalledAt(app),
    ...resolveSubmittedName(app),
    ...resolveResetCounts(app),
    ...resolveManualVerification(app),
    ...resolveNameMismatch(app),
  };
}
